/**
 * Admin allow-list — controls who can access /admin (board curation,
 * drops moderation, etc.).
 *
 * Resolution order (most → least preferred):
 *   1. `ADMIN_ADDRESSES` env var (comma-separated `0x…` list). Set
 *      this on Railway/Vercel to rotate the operator roster without
 *      redeploying. Empty / unset means "use the bootstrap list only".
 *   2. `BOOTSTRAP_ADMINS` constant — emergency floor. Always honoured
 *      so that wiping `ADMIN_ADDRESSES` can never lock the protocol
 *      out of its own admin surface. The single bootstrap entry is
 *      the multi-sig per the team's direction.
 *
 * Phase 4 (planned): back this with a database table + API endpoint
 *   (`GET /api/v1/admin/admins`, `POST /api/v1/admin/admins`) so a
 *   bootstrap admin can promote/demote others through the UI. The
 *   bootstrap floor below stays in place even after the DB lands.
 *
 * `BOOTSTRAP_DEVS` follows the same pattern — set `DEV_ADDRESSES`
 * to extend the dev (chat-only) roster without a deploy.
 */

const BOOTSTRAP_ADMINS = [
  '0x004939167D1aA3ba7D088d037b1980248A9C307c',
] as const satisfies readonly `0x${string}`[];

const BOOTSTRAP_DEVS = [
  '0x6b9Db1337B37426a7911e4108d27D44393B95eec',
] as const satisfies readonly `0x${string}`[];

/**
 * Board-manager allow-list — wallets that can edit any grid's
 * off-chain metadata as if they were the on-chain creator. Drives
 * both the server-side write gate (`requireGridCreator` in
 * `lib/auth.ts`) and the client-side `isCreator` UI flag (the
 * `gridPage.isCreator` memo in `hooks/use-grid-page.ts`).
 *
 * Scope is deliberately narrow: only the off-chain metadata routes
 * (`/api/grid/[ugm]/[gridId]/metadata`,
 * `/api/v1/pool-metadata/[poolId]`) treat managers as creator-equivalent.
 * On-chain creator-only actions (`addYield`, `transferGridCreator`,
 * `creatorCloseGrid`, etc.) are still gated by the contract itself,
 * so a manager-only wallet can see the manage-mode UI but any
 * on-chain call from it will revert. That asymmetry is intentional —
 * it lets a single ops wallet curate metadata across every board
 * without granting it any value-bearing on-chain authority.
 *
 * Kept independent of `BOOTSTRAP_DEVS` (which only opens chat-room
 * gating) so a future operator can have one without the other.
 */
const BOOTSTRAP_BOARD_MANAGERS = [
  '0x6b9Db1337B37426a7911e4108d27D44393B95eec',
] as const satisfies readonly `0x${string}`[];

const ADDRESS_RE = /^0x[0-9a-f]{40}$/i;

/**
 * Parse a comma- or whitespace-separated list of EVM addresses from an
 * env var, returning a deduplicated lowercase set. Invalid entries are
 * dropped silently (with a one-shot `console.warn` so a typo on a
 * Railway env never silently strips an admin).
 */
function parseAddressEnv(varName: string, raw: string | undefined): Set<string> {
  const out = new Set<string>();
  if (!raw) return out;
  const seenInvalid: string[] = [];
  for (const piece of raw.split(/[\s,]+/)) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    if (!ADDRESS_RE.test(trimmed)) {
      seenInvalid.push(trimmed);
      continue;
    }
    out.add(trimmed.toLowerCase());
  }
  if (seenInvalid.length > 0) {
    // eslint-disable-next-line no-console -- one-shot operator warning.
    console.warn(
      `[admin-allow-list] ${varName} contained invalid entries (ignored): ${seenInvalid.join(', ')}`,
    );
  }
  return out;
}

let cachedAdminSet: Set<string> | null = null;
let cachedDevSet: Set<string> | null = null;
let cachedBoardManagerSet: Set<string> | null = null;

function getAdminSet(): Set<string> {
  if (cachedAdminSet) return cachedAdminSet;
  const fromEnv = parseAddressEnv('ADMIN_ADDRESSES', process.env.ADMIN_ADDRESSES);
  for (const addr of BOOTSTRAP_ADMINS) fromEnv.add(addr.toLowerCase());
  cachedAdminSet = fromEnv;
  return cachedAdminSet;
}

function getDevSet(): Set<string> {
  if (cachedDevSet) return cachedDevSet;
  const fromEnv = parseAddressEnv('DEV_ADDRESSES', process.env.DEV_ADDRESSES);
  for (const addr of BOOTSTRAP_DEVS) fromEnv.add(addr.toLowerCase());
  cachedDevSet = fromEnv;
  return cachedDevSet;
}

function getBoardManagerSet(): Set<string> {
  if (cachedBoardManagerSet) return cachedBoardManagerSet;
  const fromEnv = parseAddressEnv(
    'BOARD_MANAGER_ADDRESSES',
    process.env.BOARD_MANAGER_ADDRESSES,
  );
  for (const addr of BOOTSTRAP_BOARD_MANAGERS) fromEnv.add(addr.toLowerCase());
  cachedBoardManagerSet = fromEnv;
  return cachedBoardManagerSet;
}

/**
 * Returns true if the given wallet address is allowed to access the
 * admin surface. Address comparison is case-insensitive (we don't
 * require the caller to pass an EIP-55 checksummed string).
 *
 * Returns false for missing / empty input so callers can pass the raw
 * `wallet.address` value without null-guarding it themselves.
 */
export function isAdminAddress(address: string | null | undefined): boolean {
  if (!address) return false;
  return getAdminSet().has(address.toLowerCase());
}

/** Read-only list of bootstrap admins, useful for diagnostics + tests. */
export function listBootstrapAdmins(): readonly `0x${string}`[] {
  return BOOTSTRAP_ADMINS;
}

/**
 * Returns true if the given wallet address is on the Dev allow-list.
 * Devs bypass chat-room gating in `verifyChatAccess` and get a "Dev"
 * chip rendered alongside their messages.
 *
 * Same null-tolerance + case-insensitivity as `isAdminAddress`.
 */
export function isDevAddress(address: string | null | undefined): boolean {
  if (!address) return false;
  return getDevSet().has(address.toLowerCase());
}

/** Read-only list of bootstrap devs, useful for diagnostics + tests. */
export function listBootstrapDevs(): readonly `0x${string}`[] {
  return BOOTSTRAP_DEVS;
}

/**
 * Returns true if the given wallet address is on the board-manager
 * allow-list. Managers are treated as creator-equivalent for the
 * off-chain metadata write surface — see `BOOTSTRAP_BOARD_MANAGERS`
 * above for the exact scope (deliberately narrower than admin).
 *
 * Same null-tolerance + case-insensitivity as `isAdminAddress` so
 * callers can pass `wallet.address` directly without null-guarding.
 *
 * Safe to import on both server and client: the env-var lookup
 * resolves to `undefined` in the browser bundle, so client callers
 * see the bootstrap list only — which is the right behaviour, since
 * the UI uses this purely to decide whether to show the metadata
 * edit affordances. The server-side `requireGridCreator` does the
 * authoritative write check against the same set.
 */
export function isBoardManagerAddress(address: string | null | undefined): boolean {
  if (!address) return false;
  return getBoardManagerSet().has(address.toLowerCase());
}

/** Read-only list of bootstrap board managers, useful for diagnostics + tests. */
export function listBootstrapBoardManagers(): readonly `0x${string}`[] {
  return BOOTSTRAP_BOARD_MANAGERS;
}
